import { formatGaugeRange, parseStringProduct } from "@/lib/catalog/shopify-catalog";
import { isPurchaseBlocked } from "@/lib/products/purchase-eligibility";
import type { ShopifyCartLine } from "@/types/shopify";

// ─────────────────────────────────────────────────────────
// What a cart line is, in words a customer can read on a phone.
//
// The row used to print the whole Shopify title on one truncated line:
// "Ultra Flex Bass Strings Medi…" — the string count and the gauges, the two
// things that distinguish one set from another, were the first casualties.
//
// Everything here comes from the VERIFIED product record through the same
// parser the rest of the storefront uses. Nothing is reconstructed from a
// truncated string, and a value that is not established is omitted rather than
// guessed. A self-contradicting record asserts no specification at all.
// ─────────────────────────────────────────────────────────

export interface CartLineDetail {
  label: string;
  value: string;
}

export interface CartLineIdentity {
  /** Short enough to read at 320px, and allowed to wrap rather than truncate. */
  name: string;
  /** Structured, purchase-critical facts. Empty when nothing is established. */
  details: CartLineDetail[];
  /** Set when the record contradicts itself; no specification is asserted. */
  warning: string | null;
  isCustom: boolean;
}

const CONFLICT_WARNING = "Specification under review — contact us before ordering.";

/**
 * Aurora's gauge names repeat the count and the range ("Medium (4 String)
 * 45-105"). Both are shown as their own details, so drop them from the name
 * rather than saying each thing twice on a narrow row. Anything else in the
 * name — "Hard Tension", "Soprano" — is left alone.
 */
function conciseGaugeName(gaugeName: string): string {
  return gaugeName
    .replace(/\s*\(\s*\d+\s*[- ]?string\s*\)/i, "")
    .replace(/\s*\b\d{1,3}\s*-\s*\d{1,3}\b\s*$/, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

export function cartLineIdentity(line: ShopifyCartLine): CartLineIdentity {
  const product = line.merchandise.product;
  const meta = parseStringProduct({
    handle: product.handle,
    title: product.title,
    productType: product.productType ?? "",
    metafields: [],
  });
  const blocked = isPurchaseBlocked(meta);
  const isCustom = line.attributes.some((attribute) => attribute.key === "config_id");

  // A concise name only when the parser actually resolved both halves and the
  // record agrees with itself; otherwise the real title, wrapped in full.
  const shortGauge = conciseGaugeName(meta.gaugeName);
  const concise = !blocked && meta.productLine && shortGauge
    ? `${meta.productLine} ${shortGauge}`
    : product.title;

  const details: CartLineDetail[] = [];
  if (!blocked) {
    if (meta.stringCount > 0) details.push({ label: "Strings", value: String(meta.stringCount) });
    const range = formatGaugeRange(meta.individualGauges);
    if (range) details.push({ label: "Gauges", value: range });
    // Classical sets are specified by tension, not gauge; ukulele by size.
    if (meta.tension) details.push({ label: "Tension", value: meta.tension });
  }

  // Selected variant options carry the finish and any other purchase-critical
  // choice. "Title" is Shopify's placeholder for a product with no options.
  for (const option of line.merchandise.selectedOptions) {
    if (option.name === "Title" || !option.value) continue;
    details.push({ label: option.name, value: option.value });
  }

  // A named custom build identifies itself; the configuration id does not.
  const setName = line.attributes.find((attribute) => attribute.key === "set_name")?.value?.trim();
  if (setName) details.push({ label: "Set name", value: setName });

  return {
    name: concise,
    details,
    warning: blocked ? CONFLICT_WARNING : null,
    isCustom,
  };
}
